import { useState } from 'react';
import { WalletError } from '@cef-ai/wallet-api-client';
import { useWallet } from '~/wallet';
import { EmailStep } from '~/routes/Register/EmailStep';
import { OtpStep } from '~/routes/Register/OtpStep';
import {
  EMAIL_CONFLICT_ANOTHER_ACCOUNT,
  EMAIL_CONFLICT_OWNER_UNKNOWN,
  emailBindErrorMessage,
} from './emailBindErrorMessage';

/**
 * Binds a verified email to the CURRENT account: address → code →
 * `PATCH /v1/accounts/me { emailToken }`.
 *
 * It reuses `Register`'s `EmailStep` and `OtpStep` rather than growing a
 * second copy of them. The two flows differ in exactly two places — what the
 * proven mailbox is then USED for (create an account vs. patch one), and the
 * copy — so the steps take their wording as props and the difference lives
 * here, in ~one screen of state, instead of in a duplicated pair of
 * components that would drift the first time either side changed.
 *
 * The `emailToken` is the same single-use artefact registration uses. There
 * is no second OTP path, which is what makes the two proofs line up: the JWT
 * on the PATCH proves the caller owns this account, the token proves they
 * control the mailbox. Neither alone is enough to move an address.
 *
 * It is mounted only once the reader asks for it — from `EmailNudge` on
 * Settings, or from `AccountIdentity` on the Account page, which is where the
 * bound address is displayed and therefore where "Change" lives. It can be
 * closed at any point: it is a card, never a gate, never an interstitial, and
 * nothing it does blocks any other screen.
 */
export function EmailBindFlow({ onBound, onCancel }: { onBound: (email: string) => void; onCancel: () => void }) {
  const { apiClient, identity } = useWallet();
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [expiresInSeconds, setExpiresInSeconds] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  async function sendCode() {
    setError(null);
    setBusy(true);
    try {
      // The response's `expiresInSeconds` is the server's live OTP TTL; it is
      // what step 2 renders. See `OtpStep.expiryPhrase`.
      const { expiresInSeconds: ttl } = await apiClient.accounts.emailStart(email.trim());
      setExpiresInSeconds(ttl ?? null);
      setCode('');
      setStep('otp');
    } catch (e) {
      setError(emailBindErrorMessage(e, 'email'));
    } finally {
      setBusy(false);
    }
  }

  async function resendCode() {
    setError(null);
    setResent(false);
    setResending(true);
    try {
      const { expiresInSeconds: ttl } = await apiClient.accounts.emailStart(email.trim());
      setExpiresInSeconds(ttl ?? null);
      setResent(true);
    } catch (e) {
      setError(emailBindErrorMessage(e, 'email'));
    } finally {
      setResending(false);
    }
  }

  /** Back to the address step carrying a reason, for the failures a new code cannot fix. */
  function restartWith(message: string) {
    setCode('');
    setResent(false);
    setStep('email');
    setError(message);
  }

  /**
   * A `409 email-conflict` says the server refused to bind the address. It
   * does NOT, on its own, say the address belongs to a STRANGER — and the most
   * likely conflicting address in this flow is the reader's OWN, in a second
   * tab whose `identity.account` predates a bind made in the first.
   *
   * So ask the one endpoint that knows: `GET /v1/accounts/me` is JWT-authed,
   * so its answer is about THIS account and nothing else. THREE outcomes, and
   * each gets the sentence that is true of it:
   *
   *   1. `me` says the address is already ours → say so, and refresh the stale
   *      account on the way out, which is what stops the second tab asking.
   *   2. `me` says it is not ours → the ownership claim is now earned, and
   *      `EMAIL_CONFLICT_ANOTHER_ACCOUNT` is the only branch allowed to use it.
   *   3. `me` did not answer → the owner is UNKNOWN, so nothing may be claimed
   *      about it. `EMAIL_CONFLICT_OWNER_UNKNOWN` states the one fact the 409
   *      did establish and offers two things the reader can do.
   *
   * Outcome 3 is the whole reason these are two strings. An earlier version
   * fell through to the ownership-asserting copy here, which put the exact
   * false statement this function exists to prevent back on the screen — via
   * the error path of the code that removed it.
   */
  async function resolveConflict(address: string) {
    let me: Awaited<ReturnType<typeof apiClient.accounts.me>>;
    try {
      me = await apiClient.accounts.me();
    } catch {
      // The lookup is a courtesy, not the operation — the reader is still owed
      // an answer. It is an answer that names no owner, because we do not know
      // one.
      restartWith(EMAIL_CONFLICT_OWNER_UNKNOWN);
      return;
    }
    identity.setAccount?.({ accountId: me.accountId, email: me.email, displayName: me.displayName });
    if (me.email && me.email.toLowerCase() === address.toLowerCase()) {
      onBound(me.email);
      return;
    }
    restartWith(EMAIL_CONFLICT_ANOTHER_ACCOUNT);
  }

  async function verifyAndBind() {
    setError(null);
    setResent(false);
    setBusy(true);
    const address = email.trim();
    try {
      const verified = await apiClient.accounts.emailVerify(address, code.trim());
      // `verified.status === 'already-registered'` is deliberately NOT
      // pre-empted here. It says the address is registered to SOME account;
      // `email/verify` is unauthenticated, so it cannot say whose. An earlier
      // version short-circuited on it and told a reader in a stale second tab
      // that their own address belonged to a stranger. Only `PATCH
      // /v1/accounts/me`, which carries the session JWT, can tell the two
      // apart — so let it, and read its answer.
      const updated = await apiClient.accounts.patchMe({ emailToken: verified.emailToken });
      // The patch returns the whole updated account and is the one way the
      // account changes WITHOUT a session response to carry it. Feed it back
      // so `identity.account` stops reporting the pre-bind email.
      identity.setAccount?.({
        accountId: updated.accountId,
        email: updated.email,
        displayName: updated.displayName,
      });
      onBound(updated.email ?? address);
    } catch (e) {
      if (e instanceof WalletError && e.code === 'email-conflict') {
        await resolveConflict(address);
        return;
      }
      // A spent token cannot be retried: a fresh code is the only way
      // forward, so go back to the address step rather than leave a "Verify"
      // button that can only fail again.
      if (e instanceof WalletError && e.code === 'email-unverified') {
        restartWith(emailBindErrorMessage(e, 'otp'));
        return;
      }
      setError(emailBindErrorMessage(e, 'otp'));
    } finally {
      setBusy(false);
    }
  }

  if (step === 'email') {
    return (
      <EmailStep
        headingLevel={2}
        title="Add an email address"
        description="We'll send a code to confirm the address, then save it on this wallet's account."
        submitLabel="Send code"
        email={email}
        onEmailChange={setEmail}
        onSubmit={sendCode}
        onCancel={onCancel}
        cancelLabel="Not now"
        busy={busy}
        error={error}
      />
    );
  }

  return (
    <OtpStep
      headingLevel={2}
      email={email.trim()}
      expiresInSeconds={expiresInSeconds}
      code={code}
      onCodeChange={setCode}
      onVerify={verifyAndBind}
      onResend={resendCode}
      onBack={() => {
        setError(null);
        setResent(false);
        setStep('email');
      }}
      busy={busy}
      resending={resending}
      resent={resent}
      error={error}
    />
  );
}
